import React, { useState } from 'react';
import { 
  DocumentIcon, 
  FolderIcon, 
  ChevronDownIcon, 
  ChevronRightIcon,
  MagnifyingGlassIcon,
  StarIcon,
  ClockIcon,
  UserIcon
} from '@heroicons/react/24/outline';

export interface DocumentReference {
  id: string;
  documentId?: string;
  title?: string;
  documentTitle?: string;
  path?: string;
  filePath?: string;
  description?: string; // ✅ Descrição do documento
  documentDescription?: string; // ✅ Descrição alternativa
  chunk?: string;
  content?: string;
  text?: string;
  score?: number;
  relevanceScore?: number;
  chunkIndex?: number; // ✅ Adicionado para compatibilidade com NewChatInDoc
  metadata?: {
    Size?: number;
    Tags?: string[];
    Author?: string;
    Updater?: string;
    FileName?: string;
    CreatedAt?: string;
    UpdatedAt?: string;
    Categories?: string[];
    ContentType?: string;
    contentType?: string;
    createdAt?: string;
    author?: string;
    fileName?: string;
    size?: number;
    updater?: string;
    updatedAt?: string;
  };
}

interface DocumentReferencesProps {
  references: DocumentReference[];
  isVisible: boolean;
  showIntelligenceInfo?: boolean;
}

export const DocumentReferences: React.FC<DocumentReferencesProps> = ({ 
  references, 
  isVisible,
  showIntelligenceInfo = true
}) => {
  const [expandedReferences, setExpandedReferences] = useState<Set<string>>(new Set());

  if (!isVisible || !references || references.length === 0) {
    return null;
  }

  const toggleReference = (referenceId: string) => {
    const newExpanded = new Set(expandedReferences);
    if (newExpanded.has(referenceId)) {
      newExpanded.delete(referenceId);
    } else {
      newExpanded.add(referenceId);
    }
    setExpandedReferences(newExpanded);
  };

  const getFileIcon = (path?: string, contentType?: string) => {
    if (!path && !contentType) return <DocumentIcon className="h-4 w-4 text-blue-500" />;
    
    // Determinar se é pasta ou arquivo baseado no path
    if (path && (path.endsWith('/') || path.endsWith('\\'))) {
      return <FolderIcon className="h-4 w-4 text-yellow-500" />;
    }
    
    // Determinar ícone baseado no tipo de conteúdo
    if (contentType) {
      if (contentType.includes('pdf')) return <DocumentIcon className="h-4 w-4 text-red-500" />;
      if (contentType.includes('word') || contentType.includes('docx')) return <DocumentIcon className="h-4 w-4 text-blue-600" />;
      if (contentType.includes('excel') || contentType.includes('xlsx')) return <DocumentIcon className="h-4 w-4 text-green-600" />;
      if (contentType.includes('image')) return <DocumentIcon className="h-4 w-4 text-purple-500" />;
    }
    
    return <DocumentIcon className="h-4 w-4 text-blue-500" />;
  };

  const formatScore = (score?: number) => {
    if (score === undefined || score === null || !Number.isFinite(score) || score <= 0)
    {
      return null;
    }

    const percent = Math.round(score * 100);
    if (percent <= 0)
    {
      return null;
    }

    return percent;
  };

  const getDisplayTitle = (reference: DocumentReference) => {
    return reference.title || reference.documentTitle || 'Documento sem título';
  };

  const getDisplayPath = (reference: DocumentReference) => {
    return reference.path || reference.filePath || 'Caminho não disponível';
  };

  const getDisplayContent = (reference: DocumentReference) => {
    const content = (reference.text || reference.content || reference.chunk || '').trim();
    if (!content)
    {
      return null;
    }

    if (content.includes('<document_metadata>'))
    {
      const metadataEndIndex = content.indexOf('</document_metadata>');
      if (metadataEndIndex !== -1)
      {
        const realContent = content.substring(metadataEndIndex + 19).trim();
        return realContent || null;
      }
    }

    return content;
  };

  const groupHasExpandableDetails = (group: { document: DocumentReference; chunks: DocumentReference[] }) => {
    const metadata = group.document.metadata;
    if (metadata?.author || metadata?.size || metadata?.CreatedAt || metadata?.ContentType || metadata?.Tags?.length || metadata?.Categories?.length)
    {
      return true;
    }

    return group.chunks.some((chunk) => getDisplayContent(chunk));
  };

  const getDisplayDescription = (reference: DocumentReference) => {
    return reference.description || reference.documentDescription || null;
  };

  const getDisplayScore = (reference: DocumentReference) => {
    return reference.relevanceScore || reference.score;
  };

  const formatFileSize = (size?: number) => {
    if (!size) return null;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  // ✅ Função para agrupar documentos duplicados (movida para depois das funções auxiliares)
  const groupReferencesByDocument = (refs: DocumentReference[]) => {
    const grouped = refs.reduce((acc, ref) => {
      const key = getDisplayTitle(ref) + '|' + getDisplayPath(ref);
      if (!acc[key]) {
        acc[key] = {
          document: ref,
          chunks: [],
          maxScore: ref.score || ref.relevanceScore || 0
        };
      }
      acc[key].chunks.push(ref);
      acc[key].maxScore = Math.max(acc[key].maxScore, ref.score || ref.relevanceScore || 0);
      return acc;
    }, {} as Record<string, { document: DocumentReference; chunks: DocumentReference[]; maxScore: number }>);

    return Object.values(grouped);
  };

  const groupedReferences = groupReferencesByDocument(references);

  return (
    <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
      <div className="flex items-center mb-3">
        <MagnifyingGlassIcon className="h-5 w-5 text-blue-600 mr-2" />
        <h4 className="text-sm font-medium text-blue-900">
          📚 Documentos de Referência ({groupedReferences.length})
          {references.length > groupedReferences.length && (
            <span className="ml-1 text-xs text-gray-600">
              ({references.length} {references.length === 1 ? 'trecho' : 'trechos'})
            </span>
          )}
        </h4>
      </div>
      
      <div className="space-y-3">
        {groupedReferences.map((group, index) => {
          const canExpand = groupHasExpandableDetails(group);

          return (
          <div key={`${group.document.id}-${index}`} className="bg-white rounded-lg border border-blue-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
            <div 
              className={`flex items-center justify-between p-3 ${canExpand ? 'cursor-pointer hover:bg-blue-50 transition-colors' : ''}`}
              onClick={() =>
              {
                if (canExpand)
                {
                  toggleReference(group.document.id);
                }
              }}
            >
              <div className="flex items-center flex-1 min-w-0">
                {getFileIcon(getDisplayPath(group.document), group.document.metadata?.ContentType || group.document.metadata?.contentType)}
                <div className="ml-3 flex-1 min-w-0">
                  <h5 className="text-sm font-medium text-gray-900 truncate">
                    {getDisplayTitle(group.document)}
                    {group.chunks.length > 1 && (
                      <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        {group.chunks.length} {group.chunks.length === 1 ? 'trecho' : 'trechos'}
                      </span>
                    )}
                  </h5>
                  <p className="text-xs text-gray-500 truncate">
                    {getDisplayPath(group.document)}
                  </p>
                  {getDisplayDescription(group.document) && (
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                      {getDisplayDescription(group.document)}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {formatScore(group.maxScore) && (
                  <span className="text-xs bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                    <StarIcon className="w-3 h-3 inline mr-1" />
                    {formatScore(group.maxScore)}% relevante
                  </span>
                )}
                
                {canExpand && (
                  <button type="button" className="text-gray-400 hover:text-gray-600 transition-colors">
                    {expandedReferences.has(group.document.id) ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </button>
                )}
              </div>
            </div>
            
            {canExpand && expandedReferences.has(group.document.id) && (
              <div className="border-t border-blue-100 p-3 bg-gray-50">
                {/* Metadados do documento */}
                <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                  {group.document.metadata?.author && (
                    <div className="flex items-center text-gray-600">
                      <UserIcon className="w-3 h-3 mr-1" />
                      <span className="truncate">{group.document.metadata.author}</span>
                    </div>
                  )}
                  
                  {group.document.metadata?.size && (
                    <div className="flex items-center text-gray-600">
                      <DocumentIcon className="w-3 h-3 mr-1" />
                      <span>{formatFileSize(group.document.metadata.size)}</span>
                    </div>
                  )}
                  
                  {group.document.metadata?.CreatedAt && (
                    <div className="flex items-center text-gray-600">
                      <ClockIcon className="w-3 h-3 mr-1" />
                      <span>{formatDate(group.document.metadata.CreatedAt)}</span>
                    </div>
                  )}
                  
                  {group.document.metadata?.ContentType && (
                    <div className="flex items-center text-gray-600">
                      <DocumentIcon className="w-3 h-3 mr-1" />
                      <span className="truncate">{group.document.metadata.ContentType}</span>
                    </div>
                  )}
                </div>
                
                {/* Tags e categorias */}
                {(group.document.metadata?.Tags || group.document.metadata?.Categories) && (
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-1">
                      {group.document.metadata.Tags?.map((tag, index) => (
                        <span key={index} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          {tag}
                        </span>
                      ))}
                      {group.document.metadata.Categories?.map((category, index) => (
                        <span key={index} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                          {category}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* ✅ Conteúdo dos chunks (múltiplos se agrupados) */}
                {group.chunks.map((chunk) => {
                  const chunkContent = getDisplayContent(chunk);
                  if (!chunkContent)
                  {
                    return null;
                  }

                  return (
                  <div key={chunk.id} className="mb-3 last:mb-0">
                    <div className="flex items-center justify-end mb-2">
                      {formatScore(chunk.score || chunk.relevanceScore) && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          {formatScore(chunk.score || chunk.relevanceScore)}% relevante
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 bg-white p-2 rounded border max-h-32 overflow-y-auto">
                      {chunkContent}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
};
